/**
 * MyLibraryChapterAIPanel.jsx — Summarise / Quiz / Listen modal for a
 * personal-library chapter.
 *
 * Feb 12 2026 user spec: *"add the summarization and quiz options to
 * My Library with possible test questions thoroughly generated in a
 * style worthy of a University exam setting. Add TTS options too and
 * include my voices as options for selection, such as Default voices,
 * App voices and HD voices."*
 *
 * Three tabs:
 *   - SUMMARY — short/medium/extended length toggle. LLM-generated,
 *               cached server-side per (chapter, length).
 *   - QUIZ    — 3 MCQs + 2 short-answer prompts, university-exam style.
 *               LLM-generated once, cached on first run.
 *   - LISTEN  — TTS player with voice-category picker (Default / HD /
 *               App). Calls /api/tts/prepare which returns a CDN-ish
 *               URL the player can stream.
 */
import React, { useEffect, useState } from 'react';
import {
  X, Loader2, ListChecks, Volume2, FileText, ChevronRight, RefreshCw,
  CheckCircle2, XCircle, Sparkles, Play, Pause,
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'summary', label: 'Summary', Icon: FileText },
  { id: 'quiz',    label: 'Quiz',    Icon: ListChecks },
  { id: 'listen',  label: 'Listen',  Icon: Volume2 },
];

const MyLibraryChapterAIPanel = ({ chapter, onClose }) => {
  const [tab, setTab] = useState('summary');

  return (
    <div
      className="fixed inset-0 z-[75] bg-black/80 backdrop-blur-md flex items-center justify-center p-3"
      onClick={onClose}
      data-testid="my-library-ai-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl bg-[#12131A] border border-white/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10">
          <div className="min-w-0">
            <p className="eyebrow text-amber-300">AI tools</p>
            <h3 className="font-display text-xl sm:text-2xl font-black text-white truncate">
              {chapter.title || 'Untitled chapter'}
            </h3>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-0.5 tabular-nums">
              {chapter.word_count || 0} words
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center flex-shrink-0"
            aria-label="Close"
            data-testid="my-library-ai-close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </header>

        <nav className="flex items-center border-b border-white/10 px-2" data-testid="my-library-ai-tabs">
          {TABS.map((t) => {
            const Icon = t.Icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-3 text-[10px] font-black uppercase tracking-[0.25em] transition ${
                  active ? 'text-amber-300 border-b-2 border-amber-300' : 'text-white/40 hover:text-white/70 border-b-2 border-transparent'
                }`}
                data-testid={`my-library-ai-tab-${t.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {tab === 'summary' && <SummaryPane chapter={chapter} />}
          {tab === 'quiz' && <QuizPane chapter={chapter} />}
          {tab === 'listen' && <ListenPane chapter={chapter} />}
        </div>
      </div>
    </div>
  );
};

// ───────────────────────── Summary ──────────────────────────
const LENGTHS = [
  { id: 'short',    label: 'Short',    hint: '~120 words' },
  { id: 'medium',   label: 'Medium',   hint: '~350 words' },
  { id: 'extended', label: 'Extended', hint: '~800 words, with outline' },
];

const SummaryPane = ({ chapter }) => {
  const [length, setLength] = useState('medium');
  const [data, setData] = useState({});  // { [length]: text }
  const [busy, setBusy] = useState(false);

  const run = async (forceRefresh = false) => {
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/personal-library/chapters/${chapter.item_id}/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ length, refresh: forceRefresh }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Could not generate summary'); return; }
      setData((cur) => ({ ...cur, [length]: d.summary }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-testid="my-library-summary-length">
        {LENGTHS.map((l) => (
          <button
            key={l.id}
            type="button"
            onClick={() => setLength(l.id)}
            className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] transition ${
              length === l.id
                ? 'border-amber-400/60 bg-amber-400/15 text-amber-200'
                : 'border-white/10 text-white/55 hover:text-white hover:border-white/30'
            }`}
            data-testid={`my-library-summary-length-${l.id}`}
          >
            {l.label}
            <span className="ml-1 normal-case tracking-normal text-white/40 font-bold">{l.hint}</span>
          </button>
        ))}
      </div>

      {!data[length] && !busy && (
        <button
          type="button"
          onClick={() => run()}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-black px-6 py-3 text-sm uppercase tracking-widest"
          data-testid="my-library-summary-generate"
        >
          <Sparkles className="w-4 h-4" /> Generate {length} summary
        </button>
      )}

      {busy && (
        <div className="py-10 text-center text-white/60 text-sm" data-testid="my-library-summary-loading">
          <Loader2 className="w-6 h-6 text-amber-300 animate-spin mx-auto mb-3" />
          Cooking up your summary…
        </div>
      )}

      {data[length] && !busy && (
        <article
          className="text-sm sm:text-base text-white/85 leading-relaxed whitespace-pre-wrap rounded-xl border border-white/10 bg-white/[0.02] p-4"
          style={{ fontFamily: '"Fraunces", Georgia, serif' }}
          data-testid="my-library-summary-text"
        >
          {data[length]}
        </article>
      )}
    </div>
  );
};

// ───────────────────────── Quiz ──────────────────────────
const QuizPane = ({ chapter }) => {
  const [quiz, setQuiz] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mcqAnswers, setMcqAnswers] = useState([]);
  const [revealed, setRevealed] = useState(false);
  const [showShortModels, setShowShortModels] = useState(false);

  useEffect(() => {
    // Auto-fetch on mount — cached server-side, so this is a 1-cost call.
    (async () => {
      setBusy(true);
      try {
        const r = await authFetch(`${BACKEND_URL}/api/personal-library/chapters/${chapter.item_id}/quiz`, { method: 'POST' });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { toast.error(d.detail || 'Could not generate quiz'); return; }
        setQuiz(d.quiz);
        setMcqAnswers(new Array(d.quiz.mcq.length).fill(-1));
      } finally {
        setBusy(false);
      }
    })();
  }, [chapter.item_id]);

  if (busy) {
    return (
      <div className="py-10 text-center text-white/60 text-sm" data-testid="my-library-quiz-loading">
        <Loader2 className="w-6 h-6 text-amber-300 animate-spin mx-auto mb-3" />
        Drafting university-style exam questions…
      </div>
    );
  }
  if (!quiz) {
    return <p className="text-rose-300 text-sm">Could not load quiz.</p>;
  }

  const mcqCorrect = revealed
    ? quiz.mcq.reduce((acc, q, i) => acc + (mcqAnswers[i] === q.answer_index ? 1 : 0), 0)
    : 0;

  return (
    <div className="space-y-5" data-testid="my-library-quiz">
      <header className="text-center">
        <p className="eyebrow text-amber-300">University-style exam</p>
        <p className="text-[11px] text-white/50 mt-1">
          {quiz.mcq.length} multiple-choice · {quiz.short_answer.length} short-answer
        </p>
      </header>

      {/* MCQ section */}
      <section className="space-y-4" data-testid="my-library-quiz-mcq">
        <h4 className="text-[10px] uppercase tracking-[0.3em] font-black text-white/70">Section A — multiple choice</h4>
        {quiz.mcq.map((q, qi) => (
          <fieldset
            key={qi}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
            data-testid={`my-library-quiz-mcq-${qi}`}
          >
            <legend className="px-2 text-[10px] uppercase tracking-widest font-black text-amber-300">
              Q{qi + 1}
            </legend>
            <p className="text-sm font-semibold text-white mb-3">{q.question}</p>
            <div className="space-y-1.5">
              {q.options.map((opt, oi) => {
                const picked = mcqAnswers[qi] === oi;
                const correct = revealed && oi === q.answer_index;
                const wrongPick = revealed && picked && oi !== q.answer_index;
                return (
                  <label
                    key={oi}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs cursor-pointer transition ${
                      correct ? 'border-emerald-400/60 bg-emerald-400/10 text-emerald-100'
                      : wrongPick ? 'border-rose-400/60 bg-rose-400/10 text-rose-100'
                      : picked ? 'border-amber-400/60 bg-amber-400/10 text-white'
                      : 'border-white/5 hover:border-white/20 text-white/80'
                    }`}
                    data-testid={`my-library-quiz-mcq-${qi}-opt-${oi}`}
                  >
                    <input
                      type="radio"
                      name={`lq-${qi}`}
                      disabled={revealed}
                      checked={picked}
                      onChange={() => {
                        const next = [...mcqAnswers]; next[qi] = oi; setMcqAnswers(next);
                      }}
                      className="accent-amber-400"
                    />
                    <span>{opt}</span>
                    {correct && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 ml-auto" />}
                    {wrongPick && <XCircle className="w-3.5 h-3.5 text-rose-300 ml-auto" />}
                  </label>
                );
              })}
            </div>
            {revealed && q.explanation && (
              <p className="mt-2 text-[11px] text-white/60 italic" data-testid={`my-library-quiz-mcq-${qi}-explanation`}>
                {q.explanation}
              </p>
            )}
          </fieldset>
        ))}

        {!revealed && (
          <button
            type="button"
            onClick={() => setRevealed(true)}
            disabled={mcqAnswers.some((a) => a < 0)}
            className="w-full rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black px-6 py-2.5 text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
            data-testid="my-library-quiz-mcq-reveal"
          >
            Reveal answers
          </button>
        )}

        {revealed && (
          <p className="text-center text-sm font-bold text-amber-200" data-testid="my-library-quiz-mcq-score">
            {mcqCorrect}/{quiz.mcq.length} correct
          </p>
        )}
      </section>

      {/* Short-answer section */}
      <section className="space-y-4" data-testid="my-library-quiz-short">
        <h4 className="text-[10px] uppercase tracking-[0.3em] font-black text-white/70">Section B — short answer</h4>
        {quiz.short_answer.map((q, qi) => (
          <div
            key={qi}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
            data-testid={`my-library-quiz-short-${qi}`}
          >
            <p className="text-[10px] uppercase tracking-widest font-black text-amber-300 mb-1">
              Q{quiz.mcq.length + qi + 1}
            </p>
            <p className="text-sm font-semibold text-white mb-2">{q.question}</p>
            <textarea
              rows={4}
              className="w-full rounded-lg bg-[#0B0C10] border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60 resize-y"
              placeholder="Type your answer here…"
              data-testid={`my-library-quiz-short-${qi}-input`}
            />
            {showShortModels && (
              <div className="mt-3 rounded-lg border border-emerald-400/30 bg-emerald-400/5 p-3" data-testid={`my-library-quiz-short-${qi}-model`}>
                <p className="text-[10px] uppercase tracking-widest font-black text-emerald-300 mb-1">Model answer</p>
                <p className="text-xs text-white/85 whitespace-pre-wrap">{q.model_answer}</p>
                {q.rubric?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-[11px] text-white/60 list-disc list-inside">
                    {q.rubric.map((r, ri) => <li key={ri}>{r}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}

        {!showShortModels && (
          <button
            type="button"
            onClick={() => setShowShortModels(true)}
            className="w-full rounded-full border border-white/15 hover:border-white/40 text-white/80 px-6 py-2.5 text-xs font-black uppercase tracking-widest inline-flex items-center justify-center gap-2"
            data-testid="my-library-quiz-short-reveal"
          >
            <Sparkles className="w-3.5 h-3.5" /> Show model answers
          </button>
        )}
      </section>
    </div>
  );
};

// ───────────────────────── Listen ──────────────────────────
const ListenPane = ({ chapter }) => {
  const [catalog, setCatalog] = useState({ default: [], hd: [], app: [] });
  const [category, setCategory] = useState('default');
  const [voiceId, setVoiceId] = useState('fable');
  const [audioUrl, setAudioUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [audioEl, setAudioEl] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/tts/voice-catalog`);
        const d = await r.json();
        setCatalog(d);
        // Pick a sensible default voice for whichever category is non-empty.
        if (d.default?.length) {
          setVoiceId('fable');
          setCategory('default');
        }
      } catch { /* leave empty */ }
    })();
  }, []);

  // When user switches category, reset voice + drop the cached blob.
  const switchCategory = (cat) => {
    setCategory(cat);
    const list = catalog[cat] || [];
    setVoiceId(list[0]?.id || '');
    setAudioUrl('');
    if (audioEl) audioEl.pause();
  };

  const generate = async () => {
    if (!voiceId) { toast.error('Pick a voice first'); return; }
    setBusy(true);
    setAudioUrl('');
    if (audioEl) audioEl.pause();
    try {
      const model = category === 'hd' ? 'tts-1-hd' : 'tts-1';
      const r = await authFetch(`${BACKEND_URL}/api/tts/prepare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: chapter.text || '',
          voice: voiceId,
          model,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) {
        toast.error(d.detail || 'TTS failed');
        return;
      }
      // /api/tts/cached/<key>.mp3 → make absolute for the audio tag.
      const full = d.url.startsWith('http') ? d.url : `${BACKEND_URL}${d.url}`;
      setAudioUrl(full);
      toast.success(`Audio ready · ${category === 'hd' ? 'HD' : category === 'app' ? 'App' : 'Default'} voice`);
    } finally {
      setBusy(false);
    }
  };

  const currentList = catalog[category] || [];

  return (
    <div className="space-y-4">
      {/* Category picker */}
      <div className="flex items-center gap-2" data-testid="my-library-listen-categories">
        {[
          { id: 'default', label: 'Default voices', count: catalog.default?.length || 0 },
          { id: 'hd',      label: 'HD voices',      count: catalog.hd?.length || 0 },
          { id: 'app',     label: 'App voices',     count: catalog.app?.length || 0 },
        ].map((c) => {
          const active = category === c.id;
          const disabled = c.count === 0;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => !disabled && switchCategory(c.id)}
              disabled={disabled}
              className={`flex-1 rounded-xl border px-3 py-2 text-[10px] font-black uppercase tracking-widest transition ${
                active ? 'border-amber-400/60 bg-amber-400/15 text-amber-200'
                : disabled ? 'border-white/5 text-white/25 cursor-not-allowed'
                : 'border-white/10 text-white/55 hover:text-white hover:border-white/30'
              }`}
              data-testid={`my-library-listen-category-${c.id}`}
            >
              {c.label}
              <span className="block mt-0.5 text-[9px] normal-case tracking-normal font-bold text-white/50">
                {disabled ? 'unavailable' : `${c.count} voice${c.count === 1 ? '' : 's'}`}
              </span>
            </button>
          );
        })}
      </div>

      {/* Voice list */}
      {currentList.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="my-library-listen-voices">
          {currentList.map((v) => {
            const active = voiceId === v.id;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setVoiceId(v.id)}
                className={`text-left rounded-xl border px-3 py-2 transition ${
                  active ? 'border-amber-400/60 bg-amber-400/10' : 'border-white/10 hover:border-white/30'
                }`}
                data-testid={`my-library-listen-voice-${v.id}`}
              >
                <p className="font-bold text-white text-sm">{v.label}</p>
                <p className="text-[10px] text-white/50">{v.description}</p>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-white/40 italic text-center py-6" data-testid="my-library-listen-empty">
          No {category} voices available yet.
        </p>
      )}

      {/* Generate + audio player */}
      <button
        type="button"
        onClick={generate}
        disabled={busy || !voiceId}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black px-6 py-3 text-sm uppercase tracking-widest"
        data-testid="my-library-listen-generate"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
        Generate audio
      </button>

      {audioUrl && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid="my-library-listen-player">
          <audio
            ref={(el) => setAudioEl(el)}
            src={audioUrl}
            controls
            className="w-full"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            data-testid="my-library-listen-audio"
          />
        </div>
      )}
    </div>
  );
};

export default MyLibraryChapterAIPanel;
